import { PagenatedResponse } from '@models/common';
import { ProductOrderMemo, ProductOrderViewModel } from '@models/productOrderView';
import { PaymentStatementsQuery } from '@stores/paymentStatementsStore';
import { apiClient } from '.';

export interface RefundProductOrdersResponse {
  targetIds: string[];
  success: number;
  failed: number;
  successIds: string[];
  failedIds: string[];
  needToNaverAdminCancelOrderIds?: string[];
}

export interface ProductOptionSalesCountItem {
  optionName: string;
  count: number;
}

export interface ProductOptionSalesCountResponse {
  sold: ProductOptionSalesCountItem[];
  refunded: ProductOptionSalesCountItem[];
  soldNaver: ProductOptionSalesCountItem[];
  refundedNaver: ProductOptionSalesCountItem[];
}

export const searchProductOrders = async (query?: PaymentStatementsQuery) => {
  return apiClient.get<PagenatedResponse<ProductOrderViewModel>>(`/admin/v1/product-orders`, {
    params: query,
  });
};

export const searchProductOrdersForSettlement = async (query?: PaymentStatementsQuery) => {
  return apiClient.get<PagenatedResponse<ProductOrderViewModel>>(`/admin/v1/product-orders/settlement`, {
    params: query,
  });
};

export const getOptionSalesCountByProductId = async (productId: string) => {
  return apiClient.get<ProductOptionSalesCountResponse>(`/admin/v1/product-orders/options/net-sales-count`, {
    params: { productId },
  });
};

export const requestRefundProductOrders = async (data: {
  paymentStatementIds: string[];
  naverOrderUids: string[];
  kakaoOrderUids: string[];
  benepiaOrderUids: string[];
}) => {
  return await apiClient.post<{
    app: RefundProductOrdersResponse;
    naver: RefundProductOrdersResponse;
    kakao: RefundProductOrdersResponse;
    benepia: RefundProductOrdersResponse;
  }>(`/admin/v1/product-orders/refund`, data);
};

export const getSearchProductOrdersExcelFile = async (query?: PaymentStatementsQuery) => {
  return apiClient.get(`/admin/v1/product-orders/export`, {
    params: { query },
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getPBProductOrdersExcelFile = async (query?: PaymentStatementsQuery) => {
  return apiClient.get(`/admin/v1/product-orders/export/pb`, {
    params: query,
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getPlannedCommerceProductOrdersExcelFile = async (query?: PaymentStatementsQuery) => {
  return apiClient.get(`/admin/v1/product-orders/export/planned`, {
    params: query,
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const getMarketingAgreedUsersExcelFile = async (query?: PaymentStatementsQuery) => {
  return apiClient.get(`/admin/v1/product-orders/export/marketing-agreed-users`, {
    params: query,
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const updateProductOrderMemo = async (data: { uid: string; memo: ProductOrderMemo }) => {
  return apiClient.put(`/admin/v1/product-orders/${data.uid}/memo`, {
    memo: data.memo,
  });
};

export const updateProductOrderBuyerInfo = async (data: {
  uid: string;
  buyerInfo: { buyer: ProductOrderViewModel['buyer']; receiver?: ProductOrderViewModel['receiver'] };
}) => {
  return apiClient.put(`/admin/v1/product-orders/${data.uid}/buyer`, {
    buyerInfo: data.buyerInfo,
  });
};

export const updateCompletionDate = async (data: { uid: string; body: { completionDate: string; memo: string } }) => {
  const uid = data.uid;
  return apiClient.post(`/admin/v1/product-orders/${uid}/completion-date`, data.body);
};

export const excludeSettlementTarget = async (data: { uid: string }) => {
  return apiClient.post(`/admin/v1/product-orders/${data.uid}/exclude-settlement-target`);
};

export const updateProductOrderSchedules = async (uid: string, channel?: string) => {
  return apiClient.put(`/admin/v1/product-orders/${uid}/schedules`, {
    channel,
  });
};

export const registerEzwelUsageComplete = async (uid: string) => {
  return apiClient.post(`/admin/v1/ezwel/orders/${uid}/usage-complete`);
};

export const registerEzwelUsageUncomplete = async (uid: string) => {
  return apiClient.post(`/admin/v1/ezwel/orders/${uid}/usage-uncomplete`);
};

/** 이지웰 3.2.22 무형상품 주문내역 조회 (디버그) */
export const inquireEzwelOrder = async (params: {
  ordNo?: string;
  startDate?: string;
  endDate?: string;
  qrybsic?: string;
  ordStCd?: string;
}) => {
  return apiClient.get(`/admin/v1/ezwel/orders/inquiry`, { params });
};

// 환불 스케줄 로그 관련 타입 정의
export interface RefundScheduleLog {
  _id: string;
  status: 'success' | 'failed';
  orderId: string;
  channel: 'app' | 'naver-store' | 'kakao-reservation';
  orderInfo?: {
    orderId: string;
    channel: string;
    buyerName?: string;
    buyerPhone?: string;
    productName?: string;
    amount?: number;
  };
  failureReason?: string;
  refundAmount?: number;
  processedAt: string;
  createdAt: string;
}

export interface RefundScheduleLogsQuery {
  status?: 'success' | 'failed';
  channel?: 'app' | 'naver-store' | 'kakao-reservation';
  buyerName?: string;
  buyerPhone?: string;
  page?: string;
  size?: string;
  startDate?: string;
  endDate?: string;
}

export const searchRefundScheduleLogs = async (query?: RefundScheduleLogsQuery) => {
  return apiClient.get<PagenatedResponse<RefundScheduleLog>>(`/admin/v1/product-orders/refund-schedule-logs`, {
    params: query,
  });
};
